import React, { useMemo, useRef, useState } from "react";
import EMOJI_GROUPS from "unicode-emoji-json/data-by-group.json";
import { Bold, Italic, Underline, Strikethrough, Code, List, ListOrdered, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RichTextInput from "@/components/RichTextInput";

// ---------------------------------------------------------------------------
// Shared chat-composer utilities, used by the main Chat page, the project
// sidecar, and the client-portal chat so every composer behaves the same.
// ---------------------------------------------------------------------------

// Flat, searchable emoji index for ":shortcode:" autocomplete. Built once from
// the same dataset the EmojiPicker uses. slug is the typeable shortcode.
export const EMOJI_INDEX = (() => {
  const out = [];
  for (const g of EMOJI_GROUPS) {
    for (const e of g.emojis) {
      out.push({ emoji: e.emoji, slug: e.slug, name: e.name });
    }
  }
  return out;
})();

// Detect a ":shortcode" being typed immediately before the caret. Returns the
// query (letters after the colon) or null when no shortcode is in progress.
export function detectShortcode(textBeforeCaret) {
  const m = (textBeforeCaret || "").match(/(?:^|\s):([a-z0-9_+-]{2,})$/i);
  return m ? m[1].toLowerCase() : null;
}

// Rank shortcode candidates: exact slug first, then slug prefix, then any
// slug/name substring. Returns up to `limit` {emoji, slug, name}.
export function searchShortcodes(query, limit = 8) {
  const q = (query || "").toLowerCase();
  if (!q) return [];
  const starts = [];
  const contains = [];
  for (const e of EMOJI_INDEX) {
    const slug = e.slug || "";
    if (slug === q) { starts.unshift(e); continue; }
    if (slug.startsWith(q)) { starts.push(e); continue; }
    if (slug.includes(q) || (e.name || "").toLowerCase().includes(q)) contains.push(e);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

// Replace the ":query" immediately before the caret with the chosen emoji.
export function replaceShortcode(value, caret, emoji) {
  const before = value.slice(0, caret);
  const after = value.slice(caret);
  const m = before.match(/(^|\s):([a-z0-9_+-]{2,})$/i);
  if (!m) return { next: value, caret };
  const start = before.length - m[2].length - 1; // index of the ':'
  const next = value.slice(0, start) + emoji + after;
  return { next, caret: start + emoji.length };
}

const WRAP = {
  bold: "**",
  italic: "*",
  underline: "++",
  strike: "~~",
  code: "`",
};

// Apply a formatting action to an input/textarea's current selection.
// Slack-like: wraps a selection, toggles off when already wrapped, inserts an
// empty marker pair (caret between) with no selection, and prefixes/strips
// list lines once (never stacking). Returns { next, selStart, selEnd }.
export function applyFormat(el, value, kind) {
  value = value || "";
  const start = typeof el?.selectionStart === "number" ? el.selectionStart : value.length;
  const end = typeof el?.selectionEnd === "number" ? el.selectionEnd : start;

  if (kind === "bullet" || kind === "ordered") {
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    let lineEnd = value.indexOf("\n", end);
    if (lineEnd === -1) lineEnd = value.length;
    const block = value.slice(lineStart, lineEnd);
    const lines = block.split("\n");
    const bulletRe = /^(\s*)[-*\u2022] +/;
    const numRe = /^(\s*)\d+\.\s+/;
    const isPrefixed = (l) => (kind === "bullet" ? bulletRe : numRe).test(l);
    const nonEmpty = lines.filter((l) => l.trim() !== "");
    const allPrefixed = nonEmpty.length > 0 && nonEmpty.every(isPrefixed);
    let newLines;
    if (allPrefixed) {
      // Toggle off — strip this list marker from each line.
      newLines = lines.map((l) => l.replace(kind === "bullet" ? bulletRe : numRe, "$1"));
    } else {
      // Strip any existing list marker first (so we never stack), then add once.
      let n = 0;
      newLines = lines.map((l) => {
        const stripped = l.replace(bulletRe, "$1").replace(numRe, "$1");
        if (stripped.trim() === "" && lines.length > 1) return stripped;
        n += 1;
        return kind === "bullet" ? `- ${stripped}` : `${n}. ${stripped}`;
      });
    }
    const newBlock = newLines.join("\n");
    const next = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
    return { next, selStart: lineStart, selEnd: lineStart + newBlock.length };
  }

  const token = WRAP[kind] || "";
  if (!token) return { next: value, selStart: start, selEnd: end };
  const tlen = token.length;

  // No selection: grab the non-space "word" around the caret and wrap that,
  // so typing "hello" then clicking Bold makes **hello** (not stray markers).
  let s = start, e = end;
  if (e === s) {
    let ws = s, we = s;
    while (ws > 0 && /\S/.test(value[ws - 1])) ws -= 1;
    while (we < value.length && /\S/.test(value[we])) we += 1;
    if (we > ws) { s = ws; e = we; }
  }
  const selected = value.slice(s, e);

  if (e > s) {
    // Markers sit just OUTSIDE the selection → unwrap (toggle off).
    if (value.slice(s - tlen, s) === token && value.slice(e, e + tlen) === token) {
      const next = value.slice(0, s - tlen) + selected + value.slice(e + tlen);
      return { next, selStart: s - tlen, selEnd: e - tlen };
    }
    // Markers are INSIDE the selection → unwrap (toggle off).
    if (selected.startsWith(token) && selected.endsWith(token) && selected.length >= tlen * 2) {
      const inner = selected.slice(tlen, selected.length - tlen);
      const next = value.slice(0, s) + inner + value.slice(e);
      return { next, selStart: s, selEnd: s + inner.length };
    }
    // Otherwise wrap, keeping the inner text selected.
    const next = value.slice(0, s) + token + selected + token + value.slice(e);
    return { next, selStart: s + tlen, selEnd: s + tlen + selected.length };
  }

  // Caret sits in whitespace (no adjacent word): if it's inside an empty pair,
  // remove it (toggle); otherwise insert an empty pair with the caret between.
  if (value.slice(s - tlen, s) === token && value.slice(s, s + tlen) === token) {
    const next = value.slice(0, s - tlen) + value.slice(s + tlen);
    const caret = s - tlen;
    return { next, selStart: caret, selEnd: caret };
  }
  const next = value.slice(0, s) + token + token + value.slice(s);
  const caret = s + tlen;
  return { next, selStart: caret, selEnd: caret };
}

// True when the draft contains any markdown the renderer will format — used to
// show a live preview so the user can see the formatting is actually applied.
export function hasFormatting(text) {
  if (!text) return false;
  if (/(\*\*|~~|\+\+|`)/.test(text)) return true;
  if (/(^|\n)\s*([-*\u2022]\s+|\d+[.)]\s+)/.test(text)) return true;
  if (/(^|\s)\*[^*\n]+\*/.test(text)) return true; // single-* italic
  return false;
}

const TOOLS = [
  { kind: "bold", Icon: Bold, title: "Bold  (Ctrl/⌘B)" },
  { kind: "italic", Icon: Italic, title: "Italic  (Ctrl/⌘I)" },
  { kind: "underline", Icon: Underline, title: "Underline  (Ctrl/⌘U)" },
  { kind: "strike", Icon: Strikethrough, title: "Strikethrough (~~text~~)" },
  { kind: "code", Icon: Code, title: "Code (`text`)" },
  { kind: "bullet", Icon: List, title: "Bulleted list" },
  { kind: "ordered", Icon: ListOrdered, title: "Numbered list" },
];

// Keyboard shortcut handler for a composer input/textarea: Cmd/Ctrl + B / I / U.
// Returns true when it handled (and applied) a shortcut. `el` is the DOM node.
export function handleFormatShortcut(e, el, value, onChange) {
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return false;
  const kind = { b: "bold", i: "italic", u: "underline" }[(e.key || "").toLowerCase()];
  if (!kind) return false;
  e.preventDefault();
  const { next, selStart, selEnd } = applyFormat(el, value ?? "", kind);
  onChange(next);
  setTimeout(() => { if (el) { el.focus(); try { el.setSelectionRange(selStart, selEnd); } catch (err) { /* noop */ } } }, 0);
  return true;
}

// Formatting toolbar. Wraps the selection in `targetRef` and pushes the new
// value through onChange, then restores focus + selection.
export function FormatToolbar({ targetRef, value, onChange, size = "sm", testIdPrefix = "fmt" }) {
  const run = (kind) => {
    const el = targetRef?.current;
    const { next, selStart, selEnd } = applyFormat(el, value ?? "", kind);
    onChange(next);
    setTimeout(() => {
      const n = targetRef?.current;
      if (n) {
        n.focus();
        try { n.setSelectionRange(selStart, selEnd); } catch (e) { /* noop */ }
      }
    }, 0);
  };
  const btn = size === "xs" ? "h-6 w-6" : "h-7 w-7";
  const icon = size === "xs" ? 12 : 14;
  return (
    <div className="flex items-center gap-0.5" data-testid={`${testIdPrefix}-toolbar`}>
      {TOOLS.map(({ kind, Icon, title }) => (
        <button
          key={kind}
          type="button"
          title={title}
          onMouseDown={(e) => { e.preventDefault(); run(kind); }}
          className={`${btn} flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 hover:text-[#F54800] transition-colors`}
          data-testid={`${testIdPrefix}-${kind}`}
        >
          <Icon size={icon} />
        </button>
      ))}
    </div>
  );
}

// Presentational :shortcode: dropdown. Parent owns detection + selection.
export function ShortcodeMenu({ items, activeIndex, onPick, className = "" }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      className={`bg-white border border-slate-200 shadow-lg rounded-sm overflow-hidden z-30 ${className}`}
      data-testid="shortcode-dropdown"
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">Emoji</div>
      {items.map((e, i) => (
        <button
          key={e.slug}
          type="button"
          onMouseDown={(ev) => { ev.preventDefault(); onPick(e); }}
          className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${i === activeIndex ? "bg-[#F54800]/10" : "hover:bg-slate-50"}`}
          data-testid={`shortcode-item-${e.slug}`}
        >
          <span className="text-lg leading-none">{e.emoji}</span>
          <span className="text-slate-700">:{e.slug}:</span>
        </button>
      ))}
    </div>
  );
}

// WYSIWYG chat composer for the client portal: formatting shows inline (no
// markers). onSend receives the serialized markdown; the editor self-clears.
export function RichChatComposer({ onSend, onTyping, placeholder, disabled, sendClass = "bg-[#F54800] hover:bg-[#D63E00]", testIdPrefix = "chat" }) {
  const richRef = useRef(null);
  const submit = () => {
    const md = richRef.current ? richRef.current.getMarkdown() : "";
    if (!md.trim()) return;
    onSend(md);
    richRef.current?.clear();
    if (onTyping) onTyping(false);
  };
  return (
    <div className="flex items-end gap-2 w-full">
      <div className="flex-1 min-w-0">
        <RichTextInput ref={richRef} users={[]} onSubmit={submit} onTyping={onTyping} placeholder={placeholder} disabled={disabled} size="xs" testIdPrefix={testIdPrefix} inputTestId={`${testIdPrefix}-input`} />
      </div>
      <Button onClick={submit} disabled={disabled} className={`rounded-sm text-white relative z-20 self-end ${sendClass}`} data-testid={`${testIdPrefix}-send`}><Send size={14} /></Button>
    </div>
  );
}
